import { createI18n } from 'vue-i18n'
import type { WritableComputedRef } from 'vue'
import zhCN from './locales/zh-CN.json'

/** 支持的语言列表 */
export const SUPPORTED_LANGS = ['zh-CN', 'en-US', 'zh-TW', 'th-TH'] as const

/** locale 联合类型（用于 createI18n 泛型，避免 locale.value 被收窄为字面量） */
export type Locale = (typeof SUPPORTED_LANGS)[number]

/** 将短语言码映射为 i18n locale */
export const SHORT_LANG_MAP: Record<string, Locale> = {
  'zh': 'zh-CN',
  'en': 'en-US',
  'zh-TW': 'zh-TW',
  'th': 'th-TH',
}

/** 按需加载非默认语言包 */
export async function loadLocale(lang: Locale): Promise<Record<string, any>> {
  switch (lang) {
    case 'en-US': return (await import('./locales/en-US.json')).default
    case 'zh-TW': return (await import('./locales/zh-TW.json')).default
    case 'th-TH': return (await import('./locales/th-TH.json')).default
    default: return (await import('./locales/zh-CN.json')).default
  }
}

/** 检测应使用的语言（优先级：URL → localStorage → navigator → 默认 zh-CN） */
export function detectLanguage(urlLang?: string | null): Locale {
  if (urlLang) {
    return SHORT_LANG_MAP[urlLang] || 'zh-CN'
  }
  const stored = localStorage.getItem('i18nextLng')
  if (stored && (SUPPORTED_LANGS as readonly string[]).includes(stored)) {
    return stored as Locale
  }
  const navLang = navigator.language
  if (navLang?.startsWith('zh')) return 'zh-CN'
  if (navLang?.startsWith('th')) return 'th-TH'
  if (navLang?.startsWith('en')) return 'en-US'
  return 'zh-CN'
}

const i18n = createI18n({
  legacy: false,
  locale: 'zh-CN',
  fallbackLocale: 'zh-CN',
  messages: {
    'zh-CN': zhCN,
  },
})

/** 切换语言：按需加载非默认语言包后切换（保留 localStorage 'i18nextLng' 兼容旧版） */
export async function setLanguage(lang: Locale): Promise<void> {
  if (lang !== 'zh-CN') {
    const bundle = await loadLocale(lang)
    i18n.global.setLocaleMessage(lang, bundle as typeof zhCN)
  }
  // createI18n 会把 locale 收窄为初始值 'zh-CN'，运行期需切换到其它 Locale，故显式放宽 ref 类型
  ;(i18n.global.locale as WritableComputedRef<Locale>).value = lang
  localStorage.setItem('i18nextLng', lang)
}

export default i18n
